import React, { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import moment from 'moment'
import { toast } from 'react-toastify'
import {
  messagingService, type ApiMessage, type ApiThread, type ApiWorklistRow,
  type AwaitingWorklistResponse,
} from '../services/messaging.service'

/**
 * "My Messages" — the physician's cross-patient inbox. ANSWER INLINE.
 *
 * WHY INLINE, NOT CLICK-THROUGH (RD 2026-07-29, from the live panel). The first
 * cut was a list you could only click FROM — every reply meant navigating into
 * the patient's chart. At ~100 messages a day, most are quick ("yes, that's
 * fine", "take it with food"), and the context switch for each is a waste. So a
 * row EXPANDS in place: the conversation and a reply box open under it, you send
 * a signed reply without leaving the inbox, and it drops off the queue. "Open in
 * chart" stays for the ones that genuinely need the full record.
 *
 * PORTAL_MESSAGING_ADR D13/D15. Physician-only (endpoint 403s a navigator).
 * Worst-first (server sorts breach-first then soonest-due). Replies are SIGNED
 * (D14) — each message shows its author. Breach SORTS/HIGHLIGHTS, never gates (D4).
 */

function when(ts?: number | null) {
  return ts ? moment.unix(ts).format('MMM D, h:mm a') : ''
}

/** Who signed a message (D14): the cardiologist by name, or a care-team relay —
 *  never one shown as the other. */
function signatureLabel(m: ApiMessage): string {
  if (m.author_kind === 'patient') return 'Patient'
  const role = m.author_role === 'navigator' ? 'Care Team'
    : m.author_role === 'provider' ? 'Cardiologist' : 'Care Team'
  return m.author_name ? `${m.author_name} · ${role}` : 'Care team'
}

/** Tell the nav badge the queue changed, so its count refreshes without a
 *  navigation (the slidebar listens for this). */
function announceChange() {
  window.dispatchEvent(new CustomEvent('messages:changed'))
}

const MyMessagesPage: React.FC = () => {
  const [data, setData] = useState<AwaitingWorklistResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [openId, setOpenId] = useState<number | null>(null)   // expanded thread
  const [thread, setThread] = useState<ApiThread | null>(null) // fetched conversation
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setData(await messagingService.getAwaitingWorklist())
      setError(null)
    } catch (e: any) {
      if (e?.response?.status === 403) setError('Messages are available to physicians only.')
      // Never render a failed load as "no messages" — that reads as "nobody is
      // waiting" when nothing was fetched.
      else setError(e?.response?.data?.error?.message || 'Could not load messages.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const toggle = async (row: ApiWorklistRow) => {
    if (openId === row.id) { setOpenId(null); setThread(null); return }
    setOpenId(row.id); setThread(null); setDraft('')
    if (!row.patient_ref_id) return
    try {
      setThread(await messagingService.getThread(row.patient_ref_id, row.id))
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not open that conversation.')
    }
  }

  const send = async (row: ApiWorklistRow, close: boolean) => {
    const body = draft.trim()
    if (!body || busy || !row.patient_ref_id) return
    setBusy(true)
    try {
      await messagingService.reply(row.patient_ref_id, row.id, body, close)
      setDraft(''); setOpenId(null); setThread(null)
      await refresh()          // answered → the row drops off the queue
      announceChange()         // keep the nav badge in sync
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not send the reply.')
    } finally {
      setBusy(false)
    }
  }

  const close = async (row: ApiWorklistRow) => {
    if (busy || !row.patient_ref_id) return
    setBusy(true)
    try {
      // Resolve WITHOUT a reply — the message needed none (D16). Clears the clock
      // and closes the thread; the patient can reopen by replying.
      await messagingService.closeThread(row.patient_ref_id, row.id)
      if (openId === row.id) { setOpenId(null); setThread(null) }
      await refresh()
      announceChange()
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not close that thread.')
    } finally {
      setBusy(false)
    }
  }

  const openInChart = (row: ApiWorklistRow) => {
    if (!row.patient_ref_id) return
    // A NEW WINDOW — going to the chart is optional (you can answer inline), and
    // it must never cost you your place in the queue (RD 2026-07-29).
    window.open(
      `/patients/${encodeURIComponent(row.patient_ref_id)}/details?tab=messages`,
      '_blank', 'noopener',
    )
  }

  const rows = data?.threads ?? []

  return (
    <Box sx={{ p: '1.1rem 1.25rem' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>My Messages</Typography>
        {data && <Chip size="small" label={data.awaiting_count} sx={{ height: 20, fontSize: '0.7rem' }} />}
        {data && data.breached_count > 0 && (
          <Chip size="small" color="error" label={`${data.breached_count} overdue`}
                sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
      </Stack>

      <Paper sx={{ backgroundColor: 'var(--color-body-background)' }}>
        {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
        {!loading && error && (
          <Box sx={{ p: 2 }}><Typography variant="body2" color="error">{error}</Typography></Box>
        )}
        {!loading && !error && rows.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
              You're caught up — no patient is waiting on a reply.
            </Typography>
          </Box>
        )}

        {!loading && !error && rows.length > 0 && (
          <Box sx={{ maxHeight: 'calc(100vh - 12rem)', overflowY: 'auto' }}>
            {rows.map((row) => {
              const expanded = openId === row.id
              return (
                <Box key={row.id} sx={{ borderBottom: '1px solid var(--color-border)' }}>
                  {/* the row */}
                  <Box onClick={() => toggle(row)}
                       sx={{ px: 1.75, py: 1.1, cursor: 'pointer',
                             backgroundColor: expanded ? 'var(--color-background-subtle)' : 'transparent',
                             '&:hover': { backgroundColor: 'var(--color-background-subtle)' } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{row.patient_label}</Typography>
                      {row.sla_breached && (
                        <Chip size="small" label="overdue" color="error" sx={{ height: 17, fontSize: '0.62rem' }} />
                      )}
                      <Box flex={1} />
                      <Typography sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap',
                                        color: row.sla_breached ? 'var(--color-error, #d32f2f)' : 'var(--color-text-secondary)' }}>
                        {row.awaiting_reply_since
                          ? `waiting ${moment.unix(row.awaiting_reply_since).fromNow(true)} · due ${when(row.sla_due_on)}`
                          : ''}
                      </Typography>
                      {/* One-click resolve for a confirmation — no reply needed (D16). */}
                      <Tooltip title="Close — no reply needed">
                        <IconButton size="small" disabled={busy}
                          onClick={(e) => { e.stopPropagation(); void close(row) }}
                          sx={{ p: 0.25, ml: 0.25 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography sx={{ fontSize: '0.8rem', mt: 0.25, color: 'var(--color-text-secondary)',
                                      overflow: 'hidden', textOverflow: 'ellipsis',
                                      whiteSpace: expanded ? 'normal' : 'nowrap' }}>
                      {row.display_label}{row.preview ? ` — ${row.preview}` : ''}
                    </Typography>
                  </Box>

                  {/* expanded: the conversation + an inline reply box */}
                  {expanded && (
                    <Box sx={{ px: 1.75, pb: 1.5, pt: 0.5 }}>
                      {!thread && <Box sx={{ py: 1.5, textAlign: 'center' }}><CircularProgress size={16} /></Box>}
                      {thread && (
                        <>
                          <Stack spacing={0.75} sx={{ mb: 1 }}>
                            {(thread.messages || []).map((m) => (
                              <Box key={m.id}
                                   sx={{ alignSelf: m.author_kind === 'patient' ? 'flex-start' : 'flex-end',
                                         maxWidth: '85%' }}>
                                <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1,
                                           border: '1px solid var(--color-border)',
                                           backgroundColor: m.author_kind === 'patient'
                                             ? 'var(--color-background-subtle)' : 'transparent' }}>
                                  <Typography sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.62rem', color: 'var(--color-text-secondary)', mt: 0.2 }}>
                                  {signatureLabel(m)} · {when(m.created_on)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                          <Divider sx={{ mb: 1 }} />
                          <TextField fullWidth multiline minRows={2} size="small" value={draft}
                                     placeholder="Reply to the patient"
                                     onClick={(e) => e.stopPropagation()}
                                     onChange={(e) => setDraft(e.target.value)} />
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                            <Button variant="contained" size="small" disabled={busy || !draft.trim()}
                                    onClick={() => void send(row, false)} sx={{ textTransform: 'none' }}>
                              Send
                            </Button>
                            <Button variant="outlined" size="small" disabled={busy || !draft.trim()}
                                    onClick={() => void send(row, true)} sx={{ textTransform: 'none' }}>
                              Send &amp; close
                            </Button>
                            <Button variant="text" size="small" disabled={busy}
                                    onClick={() => void close(row)} sx={{ textTransform: 'none' }}>
                              Close — no reply
                            </Button>
                            <Box flex={1} />
                            <Button size="small" onClick={() => openInChart(row)}
                                    sx={{ textTransform: 'none' }}>
                              Open in chart
                            </Button>
                          </Stack>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default MyMessagesPage
